// Per-criterion entity vs cohort overlay for the top 8 most-failing
// criteria: cohort baseline as a muted ghost bar, entity bar colored
// by above/below the cohort.

import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { Card } from '@/components/cards/Card'
import { apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { EntityClickContext } from '../../helpers/dashboardContexts'
import { buildDashboardParams, useDashboardOrgId } from '../../helpers/dashboardParams'
import { useDashboardFetch } from '../../hooks/useDashboardFetch'
import { usePickedEntity } from '../../helpers/cohort'
import { escapeHtml } from '../../helpers/format'
import { ChartWidgetCard, axisTooltip, hBarBase } from '../chartWidget'

export const PerEntityDeltaWidget = ({ scope }) => {
  // Plain filters (NOT useFiltersForPickedScope): the picker's dim must
  // stay stripped so by_criteria returns the org-wide cohort baseline.
  const filters = useSelector((state) => state.qaCopilot.filters)
  const orgId = useDashboardOrgId()
  const pickedEntity = usePickedEntity(scope)
  const clickEntity = useContext(EntityClickContext)

  const url = orgId
    ? `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${buildDashboardParams(filters, orgId).toString()}`
    : null
  const { data, loading } = useDashboardFetch(url, [orgId, scope, filters])

  const rows = useMemo(() => {
    const criteria = Array.isArray(data) ? data : data?.items || []
    if (isEmpty(criteria) || !pickedEntity) {
      return []
    }
    return criteria.slice(0, 8).map((criterion) => {
      const cohortFailPct = parseFloat(criterion.failing_percent ?? 0)
      const cohortPassPct = 100 - cohortFailPct
      let entityPassPct = null
      if (scope === 'agent') {
        const userId = pickedEntity.value
        const infractor = (criterion.top_infractors || []).find(
          (row) => String(row.user_id) === String(userId)
        )
        if (infractor && !isNil(infractor.personal_failure_rate)) {
          entityPassPct = 100 - parseFloat(infractor.personal_failure_rate)
        }
      } else {
        // scorecard / campaign: cohort row already represents the picker's scoped view.
        entityPassPct = cohortPassPct
      }
      return {
        name: criterion.criteria_name || criterion.name,
        criteriaId: criterion.criteria_id,
        scorecardSid: criterion.scorecard_config_sid,
        entityPassPct,
        cohortPassPct,
      }
    })
    // entityPassPct stays null when the entity isn't in top_infractors
    // (useful "not failing much" signal); the tooltip explains it.
  }, [data, pickedEntity, scope])

  const option = useMemo(() => {
    if (isEmpty(rows)) {
      return {}
    }
    // Click payload embedded on every data point (both series) -- read
    // from p.data in handlers, never closures.
    const drillFor = (row) => ({
      criteria_id: row.criteriaId,
      name: row.name,
      scorecard_config_sid: row.scorecardSid,
    })
    return {
      tooltip: axisTooltip((params) => {
        const row = params?.[0]?.data?.row
        if (!row) {
          return ''
        }
        const entityLine = isNil(row.entityPassPct)
          ? "Not in this criterion's top failures — likely few or no fails here"
          : `This ${scope}: ${row.entityPassPct.toFixed(0)}% pass`
        return `${escapeHtml(row.name)}<br/>${entityLine}<br/>Average: ${row.cohortPassPct.toFixed(0)}% pass<br/><em>Click for details</em>`
      }),
      ...hBarBase(
        rows.map((row) => row.name),
        {
          left: 150,
          right: 48,
          top: 8,
          xAxis: { max: 100, axisLabel: { fontSize: 10, formatter: '{value}%' } },
        }
      ),
      series: [
        {
          // Cohort baseline ghost bar (rendered behind the entity bar).
          name: 'Average',
          type: 'bar',
          cursor: 'pointer',
          barWidth: '65%',
          z: 1,
          silent: false,
          itemStyle: { color: CHART.MUTED, opacity: 0.35, borderRadius: [0, 3, 3, 0] },
          data: rows.map((row) => ({
            value: parseFloat(row.cohortPassPct.toFixed(1)),
            row,
            drill: drillFor(row),
          })),
        },
        {
          name: 'Selected',
          type: 'bar',
          cursor: 'pointer',
          barWidth: '65%',
          barGap: '-100%', // overlay on the ghost bar
          z: 2,
          data: rows.map((row) => ({
            value: isNil(row.entityPassPct) ? null : parseFloat(row.entityPassPct.toFixed(1)),
            row,
            drill: drillFor(row),
            itemStyle: {
              color: row.entityPassPct >= row.cohortPassPct ? CHART.SUCCESS : CHART.CRITICAL,
              borderRadius: [0, 3, 3, 0],
            },
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: ({ value }) => (isNil(value) ? '—' : `${value}%`),
          },
        },
      ],
    }
  }, [rows, scope])

  // Custom handler: reads both drill and picked off p.data to scope the
  // criterion drawer to the picked agent.
  const events = useMemo(
    () => ({
      click: (params) => {
        const drill = params?.data?.drill
        if (!drill?.criteria_id || !clickEntity) {
          return
        }
        const payload = { ...drill }
        const picked = params?.data?.picked
        if (picked) {
          payload.agentContext = picked
        }
        clickEntity({ type: 'criterion', payload })
      },
    }),
    [clickEntity]
  )

  // The picked agent rides on the data points too, for the same
  // read-from-p.data reason as the drill payload.
  const optionWithPicked = useMemo(() => {
    if (isEmpty(option) || scope !== 'agent' || !pickedEntity?.value) {
      return option
    }
    const picked = { user_id: pickedEntity.value, full_name: pickedEntity.label }
    return {
      ...option,
      series: option.series.map((series) => ({
        ...series,
        data: series.data.map((datum) => ({ ...datum, picked })),
      })),
    }
  }, [option, scope, pickedEntity])

  if (loading) {
    return (
      <Card>
        <div className="entity-drawer-empty">Loading…</div>
      </Card>
    )
  }

  return (
    <ChartWidgetCard
      title="Per-criterion pass rate vs average"
      info="For each criterion, how the selected entity's pass rate compares to the cohort average — bars right of center beat the average, left trail it."
      hint="Click a criterion to open its drawer"
      empty={isEmpty(rows)}
      emptyText="No criterion data available."
      option={optionWithPicked}
      events={events}
      height={`${rows.length * 36 + 56}px`}
    />
  )
}
